import { forwardRef, useId } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { cn } from '@shared/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: readonly SelectOption[];
  /** Message d'erreur — typiquement fourni par react-hook-form (fieldState.error). */
  error?: string;
  /** Indication non bloquante, par exemple lorsqu'une saisie manuelle est requise. */
  warning?: string;
}

/**
 * Primitive de liste déroulante, compatible react-hook-form
 * (transmission de la ref et des props natives via `{...register('champ')}`).
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, error, warning, id, className, ...props },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={selectId} className="text-sm font-medium text-slate-700">
          {label}
        </label>
      ) : null}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${selectId}-error` : warning ? `${selectId}-warning` : undefined}
        className={cn(
          'h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm',
          'focus:border-emerald-500 focus:outline-2 focus:outline-emerald-600',
          error && 'border-red-500 focus:border-red-500 focus:outline-red-600',
          warning && !error && 'border-amber-400 bg-amber-50/40',
          className,
        )}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error ? (
        <p id={`${selectId}-error`} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : warning ? (
        <p id={`${selectId}-warning`} className="text-xs text-amber-700">
          {warning}
        </p>
      ) : null}
    </div>
  );
});
